import { Image } from 'expo-image';
import { useCallback, useMemo, useState } from 'react';
import { Platform, View, useWindowDimensions } from 'react-native';

import {
  Button,
  IconInfo,
  IconTrophyOutline,
  Text,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';

import { StepsProgress } from '../steps-progress';

export interface ChallengeCardProps {
  image: string;
  title: string;
  description: string;
  onPress: () => Promise<void> | void;
  onPressInfo: () => void;
  buttonText?: string;
  badge?: string;
  progress?: {
    current: number;
    total: number;
    type?: 'percentage' | 'steps';
  } | null;
}

export function ChallengeCard({
  image,
  title,
  description,
  onPress,
  onPressInfo,
  buttonText,
  badge,
  progress,
}: ChallengeCardProps) {
  const styles = useMqStyles();
  const [isLoading, setIsLoading] = useState(false);
  const itemWidth = useChallengeCardWidth();

  const handleOnPress = useCallback(async () => {
    try {
      setIsLoading(true);
      onPress && (await onPress());
    } finally {
      setIsLoading(false);
    }
  }, [onPress]);

  const challengeSummaryForA11y = useMemo(() => {
    const parts = ['Challenge', badge, title, description].filter(Boolean) as string[];
    if (progress) {
      parts.push(`Progress: step ${progress.current} of ${progress.total}`);
    }
    return parts.join('. ');
  }, [badge, title, description, progress]);

  return (
    <View style={[{ width: itemWidth }, styles.card]}>
      {/* Decorative on a11y: full copy lives on the summary region so tab/VO don’t land here first. */}
      <Image
        accessibilityElementsHidden
        accessible={false}
        alt=""
        importantForAccessibility="no-hide-descendants"
        source={{ uri: image }}
        style={styles.image}
      />
      <View style={styles.content}>
        {/* Web: tabIndex 0 + focusable so keyboard/SR stop here; avoid non-standard roles (summary/article) across RN targets. */}
        <View
          accessibilityLabel={challengeSummaryForA11y}
          accessible
          focusable
          {...(Platform.OS === 'web' ? ({ tabIndex: 0 } as Record<string, unknown>) : {})}
        >
          {badge && (
            <View style={styles.badge}>
              <View
                accessible={false}
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
              >
                <IconTrophyOutline color={tokens.colors.$white} />
              </View>
              <Text.Ui
                accessible={false}
                size="sm"
                weight="semibold"
                style={{ color: tokens.colors.$white }}
              >
                {badge}
              </Text.Ui>
            </View>
          )}
          <Text.Heading accessible={false} type="four" style={!badge && styles.titleSpacing}>
            {title}
          </Text.Heading>
          <Text.Ui accessible={false} size="sm" style={{ marginTop: 8 }}>
            {description}
          </Text.Ui>
        </View>
        {progress && (
          <StepsProgress
            style={{ marginTop: 8 }}
            currentStep={progress.current}
            totalSteps={progress.total}
            suppressAccessibilityAnnouncement
          />
        )}
        {buttonText && (
          <Button
            accessibilityLabel={`${buttonText}. ${challengeSummaryForA11y}`}
            accessibilityHint="Opens full challenge details"
            loading={isLoading}
            size="xs"
            onPress={handleOnPress}
            style={{ marginTop: 16 }}
          >
            <Button.Text>{buttonText}</Button.Text>
          </Button>
        )}
        <Button
          accessibilityLabel={`Challenge information, ${title}`}
          accessibilityHint="Opens full challenge details"
          type="ghost"
          style={styles.infoButton}
          onPress={onPressInfo}
        >
          <Button.Icon>
            <IconInfo
              accessibilityElementsHidden
              aria-hidden
              importantForAccessibility="no-hide-descendants"
            />
          </Button.Icon>
        </Button>
      </View>
    </View>
  );
}

const useMqStyles = createMqStyles({
  card: {
    $base: {
      borderColor: tokens.colors.$blackOpacity10,
      borderWidth: 1,
      borderRadius: 8,
      overflow: 'hidden',
    },
    $gteDesktop: {
      flexDirection: 'row',
      height: 253,
    },
  },
  image: {
    $base: {
      aspectRatio: 2,
    },
    $ltDesktop: {
      width: '100%',
    },
    $gteDesktop: {
      height: '100%',
    },
  },
  content: {
    $gteDesktop: {
      flex: 1,
      padding: 32,
      borderLeftWidth: 1,
      borderColor: tokens.colors.$blackOpacity10,
      justifyContent: 'center',
    },
    $ltDesktop: {
      padding: 12,
      borderTopWidth: 1,
      borderColor: tokens.colors.$blackOpacity10,
    },
  },
  badge: {
    $base: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      backgroundColor: tokens.colors.$success,
      marginBottom: 12,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 4,
      alignSelf: 'baseline',
    },
    $gteDesktop: {
      marginBottom: 8,
      paddingVertical: 8,
    },
  },
  infoButton: {
    $base: {
      position: 'absolute',
      top: 11,
      right: 12,
      margin: -8,
    },
  },
  titleSpacing: {
    $ltDesktop: {
      marginRight: 36,
    },
  },
});

export function useChallengeCardWidth() {
  const { width } = useWindowDimensions();
  const itemWidth = useMqSelect({ $gteDesktop: 908 }, width - 32);

  return itemWidth;
}
